import React, { useEffect, useState } from 'react';
import { Brain, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

/**
 * NikkiMemoryCard — what Nikki has remembered about the user.
 *
 * The user can review and forget any fact. Storing memory only on the
 * user's say-so + giving them deletion control is a trust requirement
 * for an "intelligent friend".
 */

const CATEGORY_META = {
  pain:       { label: 'Pain',       color: '#f87171' },
  preference: { label: 'Preference', color: '#22d3ee' },
  goal:       { label: 'Goal',       color: '#CCFF00' },
  win:        { label: 'Win',        color: '#a3e635' },
  loss:       { label: 'Loss',       color: '#fb923c' },
  context:    { label: 'Context',    color: '#a855f7' },
};

const NikkiMemoryCard = () => {
  const [facts, setFacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.getMemoryFacts();
      setFacts(r.data?.facts || []);
    } catch {
      setFacts([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const forget = async (id) => {
    if (!window.confirm('Forget this? Nikki won\'t reference it again.')) return;
    try {
      await api.deleteMemoryFact(id);
      toast.success('Forgotten.');
      await reload();
    } catch { toast.error('Could not delete. Try again?'); }
  };

  return (
    <div className="hpme-card p-5" data-testid="nikki-memory-card">
      <div className="hpme-card-corner" />
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-primary" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-primary font-secondary font-bold">NIKKI REMEMBERS</span>
        </div>
        <span className="text-[10px] text-zinc-500">{facts.length} fact{facts.length === 1 ? '' : 's'}</span>
      </div>
      {loading ? (
        <p className="text-xs text-zinc-500">loading…</p>
      ) : facts.length === 0 ? (
        <p className="text-xs text-zinc-500 leading-relaxed">
          Chat with Nikki — she'll quietly remember the stuff that matters
          (pain, preferences, goals, wins). You can forget anything anytime.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="nikki-memory-list">
          {facts.map(f => {
            const meta = CATEGORY_META[f.category] || CATEGORY_META.context;
            return (
              <li key={f.id} className="flex items-start gap-2 group" data-testid={`memory-fact-${f.id}`}>
                <span
                  className="text-[9px] uppercase tracking-wider font-secondary font-bold px-1.5 py-0.5 rounded-sm mt-0.5"
                  style={{ color: meta.color, background: `${meta.color}15`, border: `1px solid ${meta.color}40` }}
                >
                  {meta.label}
                </span>
                <p className="flex-1 text-xs text-zinc-300 leading-relaxed">{f.fact}</p>
                <button
                  onClick={() => forget(f.id)}
                  className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400 transition-opacity"
                  aria-label="forget"
                  data-testid={`memory-forget-${f.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default NikkiMemoryCard;
